import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../Styles/communitymember.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const API_BASE_URL = "";

// Fields shown in the Add / Edit / View modals (same order as the join form)
const memberFields = [
  { name: "fullName", label: "Full Name", type: "text" },
  { name: "gender", label: "Gender", type: "text" },
  { name: "contact", label: "Contact Number", type: "text" },
  { name: "personalEmail", label: "Personal Email", type: "email" },
  { name: "officialEmail", label: "Official Email", type: "email" },
  { name: "linkedIn", label: "LinkedIn Profile URL", type: "text" },
  { name: "designation", label: "Current Designation", type: "text" },
  { name: "company", label: "Organization Name", type: "text" },
  { name: "industry", label: "Nature of Industry", type: "text" },
  { name: "experience", label: "Total Professional Experience", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "membershipCategory", label: "Membership Category", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "city", label: "City", type: "text" },
  { name: "reason", label: "Primary Reason for Joining Vanakkam HRD", type: "text" },
  { name: "referralSource", label: "Referral Source", type: "text" },
];

const emptyForm = () =>
  Object.fromEntries(memberFields.map((f) => [f.name, ""]));

const formatMembershipId = (m) =>
  m.membershipId || `VHRD${String(m.id).padStart(4, "0")}`;

export default function CommunityMembersList() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalRejected, setTotalRejected] = useState(0);

  const [addingOpen, setAddingOpen] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [viewingMember, setViewingMember] = useState(null);

  // Debounce search input so we're not hitting the DB on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 whenever the page size or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, debouncedQuery]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
      });
      if (debouncedQuery) params.set("search", debouncedQuery);

      const res = await fetch(`${API_BASE_URL}/api/community-members?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load community members");
      const data = await res.json();

      setMembers(data.members || []);
      setTotalPages(data.totalPages || 1);
      setTotalMembers(data.total || 0);
      setTotalApproved(data.totalApproved || 0);
      setTotalRejected(data.totalRejected || 0);
      setSelected([]);
      setSelectAll(false);
      setError(null);
    } catch (err) {
      console.error(err);
      setMembers([]);
      setError("Failed to load community members. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedQuery]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : members.map((m) => m.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---- Delete ----
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Community Member?",
      text: "Are you sure you want to delete this member? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/community-members/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      await fetchMembers();
      Swal.fire({
        title: "Deleted!",
        text: "The member has been deleted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Failed to delete community member. Please try again.",
        icon: "error",
      });
    }
  };

  // ---- Approve / Reject ----
  const handleStatusChange = async (id, status) => {
    const isApprove = status === "approved";
    const result = await Swal.fire({
      title: isApprove ? "Approve Member?" : "Reject Member?",
      text: isApprove
        ? "Are you sure you want to approve this member?"
        : "Are you sure you want to reject this member?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: isApprove ? "#16a34a" : "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isApprove ? "Yes, Approve" : "Yes, Reject",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community-members/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      if (!res.ok) throw new Error("Failed to update status");
      await fetchMembers();
      Swal.fire({
        title: isApprove ? "Approved!" : "Rejected!",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Failed to update status. Please try again.",
        icon: "error",
      });
    }
  };

  // ---- Add new member ----
  const openAddModal = () => {
    setAddForm(emptyForm());
    setAddingOpen(true);
  };
  const closeAddModal = () => {
    setAddingOpen(false);
    setAddForm({});
  };
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/community-members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) throw new Error("Failed to add community member");
      setCurrentPage(1);
      await fetchMembers();
      closeAddModal();
    } catch (err) {
      console.error(err);
      alert("Failed to add community member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- Edit member ----
  const openEditModal = (m) => {
    setEditingMember(m);
    setEditForm(
      Object.fromEntries(memberFields.map((f) => [f.name, m[f.name] || ""]))
    );
  };
  const closeEditModal = () => {
    setEditingMember(null);
    setEditForm({});
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community-members/${editingMember.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editForm),
        }
      );
      if (!res.ok) throw new Error("Failed to update community member");
      await fetchMembers();
      closeEditModal();
    } catch (err) {
      console.error(err);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- View member ----
  const openViewModal = (m) => setViewingMember(m);
  const closeViewModal = () => setViewingMember(null);

  // ---- Export to Excel ----
  const handleExportToExcel = () => {
    if (members.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: "There are no community members to export.",
      });
      return;
    }

    const excelData = members.map((m, index) => ({
      "S.No": (currentPage - 1) * pageSize + index + 1,
      "Membership ID": formatMembershipId(m),
      "Full Name": m.fullName || "",
      "Gender": m.gender || "",
      "Contact Number": m.contact || "",
      "Personal Email": m.personalEmail || "",
      "Official Email": m.officialEmail || "",
      "LinkedIn Profile URL": m.linkedIn || "",
      "Current Designation": m.designation || "",
      "Organization Name": m.company || "",
      "Nature of Industry": m.industry || "",
      "Total Professional Experience": m.experience || "",
      "State": m.state || "",
      "City": m.city || "",
      "Country": m.country || "",
      "Membership Category": m.membershipCategory || "",
      "Primary Reason for Joining Vanakkam HRD": m.reason || "",
      "Referral Source": m.referralSource || "",
      "Status": m.status || "pending",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Community Members");
    XLSX.writeFile(workbook, "community_members.xlsx");
  };

  return (
    <div className="cml-app">
      {/* ---------------- Main content ---------------- */}
      <main className="cml-main">
        <section className="cml-panel">
          <div className="cml-controls">
            <label className="cml-show-entries">
              Show Entries :
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <span className="cml-pill cml-pill--total">Total Members : {totalMembers}</span>

            <span className="cml-pill cml-pill--approved">Total Approvals : {totalApproved}</span>

            <span className="cml-pill cml-pill--rejected">Total Rejected : {totalRejected}</span>

            <button className="cml-pill cml-pill--export" onClick={handleExportToExcel}>
              <span className="cml-excel-icon-wrap">
                <img
                  src="/assets/excel.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--default"
                />
                <img
                  src="/assets/excel-white.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--hover"
                />
              </span>
              Export To Excel
            </button>

           {/* <button className="cml-pill cml-pill--add" onClick={openAddModal}>
  <Plus size={14} />
  Add New Community Member
</button> */}

            <div className="cml-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="cml-table-outer">
            <div className="cml-table-inner">
              {loading ? (
                <p style={{ padding: "20px" }}>Loading community members...</p>
              ) : error ? (
                <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
              ) : (
                <table className="cml-table">
                  <thead>
                    <tr>
                      <th className="cml-checkbox-col">
                        <label className="cml-select-all">
                          <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                          <span>Select All</span>
                        </label>
                      </th>
                      <th>S.No</th>
                      <th>Membership ID</th>
                      <th>Full Name</th>
                      <th>Gender</th>
                      <th>Contact Number</th>
                      <th>Personal Email</th>
                      <th>Official Email</th>
                      <th>LinkedIn Profile URL</th>
                      <th>Current Designation</th>
                      <th>Organization Name</th>
                      <th>Nature of Industry</th>
                      <th>Total Professional Experience</th>
                      <th>State</th>
                      <th>City</th>
                      <th>Country</th>
                      <th>Membership Category</th>
                      <th>Primary Reason for Joining Vanakkam HRD</th>
                      <th>Referral Source</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={21} style={{ padding: "20px", textAlign: "center" }}>
                          No community members yet.
                        </td>
                      </tr>
                    ) : (
                      members.map((m, index) => (
                        <tr key={m.id}>
                          <td className="cml-checkbox-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(m.id)}
                              onChange={() => toggleOne(m.id)}
                            />
                          </td>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{formatMembershipId(m)}</td>
                          <td>
                            <div className="cml-user-cell">
                              {m.imagePath ? (
                                <img
                                  src={`${API_BASE_URL}${m.imagePath}`}
                                  alt={m.fullName}
                                  className="cml-avatar cml-avatar--photo"
                                />
                              ) : (
                                <span className="cml-avatar">{m.fullName?.charAt(0)}</span>
                              )}
                              {m.fullName}
                            </div>
                          </td>
                          <td>{m.gender}</td>
                          <td>{m.contact}</td>
                          <td>{m.personalEmail}</td>
                          <td>{m.officialEmail}</td>
                          <td>
                            {m.linkedIn ? (
                              <a href={m.linkedIn} target="_blank" rel="noreferrer" className="cml-link">
                                View Profile
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{m.designation}</td>
                          <td>{m.company}</td>
                          <td>{m.industry}</td>
                          <td>{m.experience}</td>
                          <td>{m.state}</td>
                          <td>{m.city}</td>
                          <td>{m.country}</td>
                          <td>{m.membershipCategory}</td>
                          <td>{m.reason}</td>
                          <td>{m.referralSource}</td>
                          <td>
                            <div className="cml-status-actions">
                              <button
                                className={`cml-status-btn cml-status-btn--approve${m.status === "approved" ? " is-active" : ""}${m.status === "rejected" ? " is-locked" : ""}`}
                                disabled={m.status === "approved" || m.status === "rejected"}
                                onClick={() => handleStatusChange(m.id, "approved")}
                              >
                                <Check size={13} />
                                {m.status === "approved" ? "Approved" : "Approve"}
                              </button>
                              <button
                                className={`cml-status-btn cml-status-btn--reject${m.status === "rejected" ? " is-active" : ""}${m.status === "approved" ? " is-locked" : ""}`}
                                disabled={m.status === "approved" || m.status === "rejected"}
                                onClick={() => handleStatusChange(m.id, "rejected")}
                              >
                                <X size={13} />
                                {m.status === "rejected" ? "Rejected" : "Reject"}
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="cml-row-actions">
                              <button
                                className="cml-icon-btn cml-icon-btn--view"
                                onClick={() => openViewModal(m)}
                              >
                                <img
                                  src="/assets/view.png"
                                  alt=""
                                  className="cml-btn-icon"
                                />
                                View
                              </button>
                              <button
                                className="cml-icon-btn cml-icon-btn--edit"
                                onClick={() => openEditModal(m)}
                              >
                                <Pencil size={13} />
                                Edit
                              </button>
                              <button
                                className="cml-icon-btn cml-icon-btn--delete"
                                onClick={() => handleDelete(m.id)}
                              >
                                <Trash2 size={13} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="cml-table-footer">
            <span>
              Showing {members.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              &ndash;
              {(currentPage - 1) * pageSize + members.length} of {totalMembers} Members
            </span>
            <div className="cml-pagination">
              <button
                className="cml-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "dots-start" || p === "dots-end" ? (
                  <span key={`${p}-${i}`} className="cml-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`cml-page-btn${p === currentPage ? " active" : ""}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="cml-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- Add Modal ---------------- */}
      {addingOpen && (
        <div className="cml-modal-overlay" onClick={closeAddModal}>
          <div className="cml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cml-modal-title">Add New Community Member</h2>
            <form onSubmit={handleAddSubmit} className="cml-modal-form">
              {memberFields.map((f) => (
                <div className="cml-modal-field" key={f.name}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={addForm[f.name] || ""}
                    onChange={handleAddChange}
                    required
                  />
                </div>
              ))}
              <div className="cml-modal-actions">
                <button type="button" className="cml-modal-btn cml-modal-btn--cancel" onClick={closeAddModal}>
                  Cancel
                </button>
                <button type="submit" className="cml-modal-btn cml-modal-btn--save" disabled={saving}>
                  {saving ? "Saving..." : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- Edit Modal ---------------- */}
      {editingMember && (
        <div className="cml-modal-overlay" onClick={closeEditModal}>
          <div className="cml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cml-modal-title">Edit Community Member</h2>
            <form onSubmit={handleEditSubmit} className="cml-modal-form">
              {memberFields.map((f) => (
                <div className="cml-modal-field" key={f.name}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={editForm[f.name] || ""}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              ))}
              <div className="cml-modal-actions">
                <button type="button" className="cml-modal-btn cml-modal-btn--cancel" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="cml-modal-btn cml-modal-btn--save" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- View Modal ---------------- */}
      {viewingMember && (
        <div className="cml-modal-overlay" onClick={closeViewModal}>
          <div className="cml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cml-modal-title">Community Member Details</h2>
            <div className="cml-modal-form">
              <div className="cml-modal-field">
                <label>Membership ID</label>
                <input value={formatMembershipId(viewingMember)} readOnly />
              </div>
              {memberFields.map((f) => (
                <div className="cml-modal-field" key={f.name}>
                  <label>{f.label}</label>
                  <input value={viewingMember[f.name] || ""} readOnly />
                </div>
              ))}
              <div className="cml-modal-actions">
                <button type="button" className="cml-modal-btn cml-modal-btn--cancel" onClick={closeViewModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}